import {
  IconChevronRight,
  PopoverNavigationItem,
  cssColorValue,
  semanticColors,
} from '@spotify-internal/encore-web/core';
import React from 'react';
import styled from 'styled-components';

import {
  NavBarPopoverLink,
  NavBarPopoverLinkIconLeadingImage,
  NavBarPopoverLinkIconLeadingWrapper,
  NavBarPopoverLinkText,
  NavBarPopoverLinkTextWrapper,
  NavBarPopoverList,
  NavBarPopoverNavigationItem,
} from 'components/Masthead/Header/Popover/Shared';
import { isMastheadLink, isMastheadLinkList } from 'utils/type-guards';
import { NavBarPopoverListItem } from 'components/Masthead/Header/Popover/LinkItem';
import { isLinkDisabled, isLinkVisible } from 'components/Masthead/shared/utils';
import { MastheadHeaderData, MastheadLink, MastheadLinkList } from 'lib/sanity/types';
import { NavbarPopoverItem } from 'components/Masthead/Header/Popover/PopoverItem';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';
import { urlForImage } from 'utils/sanity';

const StyledPopoverNavigationItem = styled(PopoverNavigationItem)`
  background-color: ${cssColorValue(semanticColors.backgroundTintedBase)};
  border-radius: 8px;
  padding: 8px 12px;
`;

const StyledNavBarPopoverList = styled(NavBarPopoverList)`
  margin-top: 0;
`;

const LinkListProfileLinks = ({
  links,
  handleProfileLinkClick,
  isLoggedIn,
}: {
  links: MastheadLinkList[];
  isLoggedIn: boolean;
  handleProfileLinkClick: (trackingID: string, uri: string) => void;
}) => {
  return links.map(item => (
    <NavbarPopoverItem
      key={item._key}
      container="sidemenu"
      isLoggedIn={isLoggedIn}
      linkListEntry={item}
      onClick={link => {
        handleProfileLinkClick(link.trackingID, link.href);
      }}
    />
  ));
};

const VisibleProfileLinks = ({
  links,
  isLoggedIn,
  handleProfileLinkClick,
}: {
  links: MastheadLink[];
  isLoggedIn: boolean;
  handleProfileLinkClick: (trackingID: string, uri: string) => void;
}) => {
  return links.map(item => (
    <NavBarPopoverListItem
      {...item}
      key={item._key}
      nestingLevel={1}
      isDisabled={isLinkDisabled(item, isLoggedIn)}
      onClick={() => {
        handleProfileLinkClick(item.trackingID, item.href);
      }}
    />
  ));
};

const SecondaryLinks = ({
  links,
  isLoggedIn,
  closeBackdrop,
  openSubMenu,
}: {
  links: Array<(MastheadLinkList | MastheadLink) & { iconUrl?: string }>;
  isLoggedIn: boolean;
  closeBackdrop: () => void;
  openSubMenu: (entries: MastheadLinkList) => void;
}) => {
  return links.map(({ iconUrl, ...item }) => {
    if (isMastheadLinkList(item)) {
      return (
        <NavBarPopoverNavigationItem
          key={item._key}
          onClick={() => {
            openSubMenu(item);
          }}
        >
          <NavBarPopoverLink
            iconLeading={
              iconUrl
                ? () => (
                    <NavBarPopoverLinkIconLeadingWrapper>
                      <NavBarPopoverLinkIconLeadingImage imgUrl={iconUrl} alt="" />
                    </NavBarPopoverLinkIconLeadingWrapper>
                  )
                : undefined
            }
            iconTrailing={() => <IconChevronRight size="small" />}
          >
            <NavBarPopoverLinkTextWrapper iconLeading={Boolean(iconUrl)} level={1}>
              <NavBarPopoverLinkText>{item.label}</NavBarPopoverLinkText>
            </NavBarPopoverLinkTextWrapper>
          </NavBarPopoverLink>
        </NavBarPopoverNavigationItem>
      );
    }

    return (
      <NavBarPopoverListItem
        {...item}
        key={item._key}
        nestingLevel={1}
        isDisabled={isLinkDisabled(item, isLoggedIn)}
        onClick={closeBackdrop}
      />
    );
  });
};

interface Props {
  closeBackdrop: () => void;
  openSubMenu: (entries: MastheadLinkList) => void;
  isLoggedIn: boolean;
  onProfileLinkClick: (identifier: string, uri: string) => void;
  headerEntry: MastheadHeaderData;
}

export const SideMenuWithProfileLinksMainNavList = ({
  closeBackdrop,
  openSubMenu,
  isLoggedIn,
  onProfileLinkClick,
  headerEntry: { profileLinks, secondaryLinks = [] },
}: Props) => {
  const {
    verticalConfig: { verticalName },
  } = useServerSideConstants();

  const visibleSecondaryLinks = secondaryLinks.reduce<Array<(MastheadLinkList | MastheadLink) & { iconUrl?: string }>>(
    (acc, item) => {
      if (isMastheadLinkList(item) || isLinkVisible(item, isLoggedIn)) {
        const iconUrl = item.image && urlForImage({ source: item.image.asset, maxWidth: 16, vertical: verticalName });
        acc.push({ ...item, iconUrl });
      }
      return acc;
    },
    [],
  );

  const linkListProfileLinks = profileLinks?.filter(isMastheadLinkList) || [];

  const visibleProfileLinks =
    profileLinks?.reduce<MastheadLink[]>((acc, item) => {
      if (isMastheadLink(item) && isLinkVisible(item, isLoggedIn)) {
        acc.push(item);
      }
      return acc;
    }, []) || [];

  const handleProfileLinkClick = (trackingID: string, uri: string) => {
    onProfileLinkClick(trackingID, uri);
    closeBackdrop();
  };

  return (
    <>
      <LinkListProfileLinks
        links={linkListProfileLinks}
        isLoggedIn={isLoggedIn}
        handleProfileLinkClick={handleProfileLinkClick}
      />
      <StyledPopoverNavigationItem>
        <StyledNavBarPopoverList>
          <SecondaryLinks
            links={visibleSecondaryLinks}
            isLoggedIn={isLoggedIn}
            closeBackdrop={closeBackdrop}
            openSubMenu={openSubMenu}
          />
          <VisibleProfileLinks
            links={visibleProfileLinks}
            isLoggedIn={isLoggedIn}
            handleProfileLinkClick={handleProfileLinkClick}
          />
        </StyledNavBarPopoverList>
      </StyledPopoverNavigationItem>
    </>
  );
};
